import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, BehaviorSubject, catchError, combineLatest, concatMap, finalize, forkJoin, from, of, switchMap } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { HrAlertService, HrHttpService, MrfApprovedByOption, MrfAvailableBudgetView, MrfDocumentView, MRF_DOCUMENT_TYPE } from '../hr-http.service';
import { isMrfReasonExit, formatMrfDateOrDash, formatMrfAmountForDisplay, normalizeMrfWorkflowStatus, formatMrfRequestStatusLabel, formatMrfOnboardingStatusLabel, getMrfRequestStatusTone, getMrfOnboardingStatusTone, resolveMrfOnboardingStatusRaw, resolveMrfRequestStatusRaw, resolveMrfQualifiedEmployeeName, resolveMrfRehiringAadharNo, isMrfRehiringFromData, resolveMrfSurname, resolveMrfTempPayrollId, resolveMrfYearsOfExperience, EMPTY_MRF_HRMS_PERMISSIONS, MrfHrmsPermissions, setMrfUploadSizeError, clearMrfUploadSizeError, getMrfUploadSizeError, MrfUploadErrorMap } from '../mrf-view-options';

@Component({
  selector: 'app-mrf-user2',
  templateUrl: './mrf-user2.component.html',
  styleUrls: ['./mrf-user2.component.scss'],
})
export class MrfUser2Component implements OnInit, OnDestroy {
  mrfData: any = {};
  budgetedPositions = '—';
  noOfHeadcount = '1';
  availableHeadcount = '—';
  budgetAmount: number | null = null;
  totalUsedBudget: number | null = null;
  totalExpectedSalBudget: number | null = null;
  availableBudgetAmount: number | null = null;

  rehirePolicyGuidelines: 'yes' | 'no' = 'yes';
  withinRehireCost: 'yes' | 'no' = 'yes';
  isBudgetedPosition: 'yes' | 'no' = 'yes';

  jobDescriptionAttached: 'yes' | 'no' = 'yes';
  justification = new FormControl('');
  workDescription = new FormControl('');

  approvedBy = new FormControl('');
  approvedBy2 = new FormControl('');
  approvedByOptions: Observable<any[]> = of([]);
  approvedByOptionsList: MrfApprovedByOption[] = [];
  private approvedByBaseList: MrfApprovedByOption[] = [];
  private readonly approvedByList$ = new BehaviorSubject<MrfApprovedByOption[]>([]);
  private reportingManagersList: Record<string, unknown>[] = [];

  justificationMaxLength = 5000;
  workDescriptionMaxLength = 5000;

  documents: any[] = [];
  aadhaarDocuments: any[] = [];
  panDocuments: any[] = [];
  examDocuments: any[] = [];
  jobDescDocuments: any[] = [];
  approvalDocuments: any[] = [];
  attachedDocuments: any[] = [];
  jobDescPendingFiles: File[] = [];
  approvedByPendingFiles: File[] = [];
  readonly maxPendingFiles = 5;
  uploadFileErrors: MrfUploadErrorMap = {};

  examTakenDisplay: 'yes' | 'no' = 'no';
  isSaving = false;
  mrfPermissions: MrfHrmsPermissions = { ...EMPTY_MRF_HRMS_PERMISSIONS };

  constructor(
    private spinner: NgxSpinnerService,
    private hrHttpService: HrHttpService,
    private route: ActivatedRoute,
    private router: Router,
    private hrAlert: HrAlertService
  ) {}

  displayReportingManager(): string {
    const label = this.hrHttpService.getMrfReportingManagerDisplayFromData(
      this.mrfData ?? {},
      this.reportingManagersList
    );
    return label || '—';
  }

  ngOnInit(): void {
    this.initFilteredOptions();
    this.loadMrfFormData();
  }

  private loadMrfFormData(): void {
    const mrfId = this.getMrfFormId();
    if (!mrfId) return;

    this.spinner.show();
    forkJoin({
      requests: this.hrHttpService.wrapMrfLoaderApi(this.hrHttpService.getMrfRequests(mrfId), null),
      assessment: this.hrHttpService.wrapMrfLoaderApi(
        this.hrHttpService.getEmployeeAssessment(mrfId),
        null
      ),
      documents: this.hrHttpService.wrapMrfLoaderApi(
        this.hrHttpService.getMrfDocumentsByMrfFormId(mrfId),
        [] as MrfDocumentView[]
      ),
      approvedBy: this.hrHttpService
        .getMrfApprovedByDetails()
        .pipe(catchError(() => of([]))),
      roles: this.hrHttpService.wrapMrfLoaderApi(this.hrHttpService.getUserRoles(), null),
    })
      .pipe(
        switchMap(({ requests, assessment, documents, approvedBy, roles }) => {
          if (roles != null) {
            this.mrfPermissions = this.hrHttpService.setMrfHrmsPermissionsFromResponse(roles);
          }
          let data = this.hrHttpService.normalizeMrfRequestsResponse(requests);
          data = this.hrHttpService.mergeEmployeeAssessmentIntoViewData(data, assessment);
          const campusId = (data?.['campusId'] ?? null) as string | number | null;
          return forkJoin({
            data: of(data),
            documents: of(documents),
            approvedBy: of(approvedBy),
            reportingManagers: this.hrHttpService.wrapMrfLoaderApi(
              this.hrHttpService.getMrfReportingManagers(campusId),
              [] as Record<string, unknown>[]
            ),
            budget: this.hrHttpService.wrapMrfLoaderApi(
              this.hrHttpService.getMrfAvailableBudgetForMrfData(data),
              null
            ),
          });
        }),
        map(({ data, documents, approvedBy, reportingManagers, budget }) => {
          this.reportingManagersList =
            this.hrHttpService.normalizeMrfReportingManagerList(reportingManagers);
          return { data, documents, approvedBy, budget };
        })
      )
      .subscribe({
      next: ({ data, documents, approvedBy, budget }) => {
        try {
          this.mrfData = data;
          this.setApprovedByBaseList(
            this.hrHttpService.parseMrfApprovedByDetailsResponse(approvedBy)
          );
          this.approvedBy.setValue('', { emitEvent: false });
          this.applyAvailableBudgetView(budget);
          this.initExamTakenFromData();
          this.applyMisFormFields();
          this.applyDocumentsFromApi(
            documents,
            this.hrHttpService.extractMrfDocumentsFromPayload(this.mrfData)
          );
          this.loadHeadCountFromData(() =>
            this.loadReplacementEmployeeDetails(() =>
              this.loadTempEmployeeDetailsIfNeeded(() => {
                this.applyBudgetedPositionDefaultFromAvailableBudget();
                this.applyDocumentsFromApi(
                  documents,
                  this.hrHttpService.extractMrfDocumentsFromPayload(this.mrfData)
                );
                this.spinner.hide();
              })
            )
          );
        } catch (err) {
          this.spinner.hide();
          console.error(err);
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
      },
    });
  }

  private getMrfFormId(): string {
    return this.route.snapshot.queryParamMap.get('mrfId') ?? '2';
  }

  loadDocuments(): void {
    const mrfId = this.getMrfFormId();
    if (!mrfId) return;

    this.spinner.show();
    this.hrHttpService
      .getMrfDocumentsByMrfFormId(mrfId)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (docs) => {
          this.applyDocumentsFromApi(
            docs,
            this.hrHttpService.extractMrfDocumentsFromPayload(this.mrfData)
          );
        },
        error: (err) => console.error(err),
      });
  }

  private applyDocumentsFromApi(
    apiDocs: MrfDocumentView[],
    embeddedDocs: MrfDocumentView[] = []
  ): void {
    this.revokeDocumentBlobUrls();
    this.documents = this.hrHttpService.mergeMrfDocumentLists(apiDocs, embeddedDocs);
    this.partitionDocuments();
  }

  ngOnDestroy(): void {
    this.spinner.hide();
    this.approvedByList$.complete();
    this.revokeDocumentBlobUrls();
  }

  private revokeDocumentBlobUrls(): void {
    for (const doc of this.documents) {
      if (doc?.blobUrl) {
        URL.revokeObjectURL(doc.blobUrl);
      }
    }
  }

  displayField(...keys: string[]): string {
    return this.fieldValue(...keys) || '—';
  }

  displayAmountField(...keys: string[]): string {
    return formatMrfAmountForDisplay(this.fieldValue(...keys));
  }

  displayEmployeeName(): string {
    const name = resolveMrfQualifiedEmployeeName(this.mrfData ?? {}, this.isExamTaken('yes'));
    return name || '—';
  }

  displaySurname(): string {
    const value = resolveMrfSurname(this.mrfData ?? {});
    return value || '—';
  }

  displayYearsOfExperience(): string {
    const value = resolveMrfYearsOfExperience(this.mrfData ?? {});
    return value || '—';
  }

  displayRehiringAadharNo(): string {
    const value = resolveMrfRehiringAadharNo(this.mrfData ?? {});
    return value || '—';
  }

  displayTempPayrollId(): string {
    const value = resolveMrfTempPayrollId(this.mrfData ?? {});
    return value || '—';
  }

  hasTempPayrollId(): boolean {
    return !!resolveMrfTempPayrollId(this.mrfData ?? {});
  }

  displayRehiringEmployeeName(): string {
    return (
      this.fieldValue('prevEmpName', 'newEmpName', 'newEmployeeName', 'employeeName', 'candidateName') ||
      '—'
    );
  }

  displayPreviousEmployeeName(): string {
    return this.fieldValue('prevEmpName') || '—';
  }

  displayDob(): string {
    return formatMrfDateOrDash(
      this.fieldValue('dob', 'dateOfBirth', 'employeeDob', 'newEmpDob')
    );
  }

  displayLastWorkingDate(): string {
    return formatMrfDateOrDash(
      this.fieldValue('lastWorkingDate', 'lastWorkingDay', 'lwd')
    );
  }

  isExamTaken(value: 'yes' | 'no'): boolean {
    return this.examTakenDisplay === value;
  }

  isPdfFile(doc: any): boolean {
    const name = this.getDocumentName(doc).toLowerCase();
    return name.endsWith('.pdf') || String(doc?.mimeType ?? '').includes('pdf');
  }

  isVideoFile(doc: any): boolean {
    const name = this.getDocumentName(doc).toLowerCase();
    return (
      /\.(mov|mp4|avi|webm|mkv)$/.test(name) ||
      String(doc?.mimeType ?? '').includes('video')
    );
  }

  private fieldValue(...keys: string[]): string {
    for (const key of keys) {
      const val = this.mrfData?.[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  get isRehiring(): boolean {
    return isMrfRehiringFromData(this.mrfData ?? {});
  }

  get rehiringPayrollIdDisplay(): string {
    return this.hrHttpService.resolveMrfRehiringPayrollId(this.mrfData ?? {});
  }

  displayRehiringDob(): string {
    return formatMrfDateOrDash(
      this.fieldValue('dob', 'dateOfBirth', 'employeeDob', 'newEmpDob', 'rehiringDob')
    );
  }

  get isReplacement(): boolean {
    const pt = String(this.mrfData?.positionType ?? '').toUpperCase();
    return pt.includes('REPLACEMENT') || pt === 'REPLACEMENT';
  }

  get isNewPosition(): boolean {
    return !this.isReplacement;
  }

  get reasonDescription(): string {
    return String(
      this.mrfData?.reasonDescription ??
        this.mrfData?.hiringReasonDesc ??
        this.mrfData?.reasonDesc ??
        ''
    ).trim();
  }

  get isReasonExit(): boolean {
    return isMrfReasonExit(this.reasonDescription);
  }

  get showMisApprovalActions(): boolean {
    if (!this.mrfPermissions.misForm) return false;
    return normalizeMrfWorkflowStatus(this.getWorkflowStatus()) === 'PENDING_AT_MIS';
  }

  get requestStatusLabel(): string {
    return formatMrfRequestStatusLabel(
      resolveMrfRequestStatusRaw(this.mrfData ?? {})
    );
  }

  get requestStatusTone(): string {
    return getMrfRequestStatusTone(resolveMrfRequestStatusRaw(this.mrfData ?? {}));
  }

  get onboardingStatusLabel(): string {
    return formatMrfOnboardingStatusLabel(
      resolveMrfOnboardingStatusRaw(this.mrfData ?? {})
    );
  }

  get onboardingStatusTone(): string {
    return getMrfOnboardingStatusTone(
      resolveMrfOnboardingStatusRaw(this.mrfData ?? {})
    );
  }

  get isPendingAtHrStage(): boolean {
    return normalizeMrfWorkflowStatus(this.getWorkflowStatus()) === 'PENDING_AT_HR';
  }

  get showHrEditMode(): boolean {
    return this.mrfPermissions.misForm && this.isPendingAtHrStage;
  }

  get canEditWorkDescAndApprovalDocs(): boolean {
    return this.showMisApprovalActions || this.showHrEditMode;
  }

  get isReadOnlyMisSection(): boolean {
    return !this.showMisApprovalActions && !this.showHrEditMode;
  }

  get isExpectedSalaryOverAvailableBudget(): boolean {
    return this.hrHttpService.isMrfExpectedSalaryOverAvailableBudget(
      this.hrHttpService.resolveMrfExpectedSalaryValue(this.mrfData ?? {}),
      this.availableBudgetAmount
    );
  }

  /** Previous replacement employee CTC used for % difference. */
  get prevRepEmpCtcValue(): number | null {
    return this.hrHttpService.resolveMrfPrevRepEmpCtc(this.mrfData ?? {});
  }

  get replacementSalaryPercentageDiff(): number | null {
    if (!this.isReplacement) return null;
    return this.hrHttpService.calculateMrfReplacementSalaryPercentageDiff(
      this.hrHttpService.resolveMrfExpectedSalaryValue(this.mrfData ?? {}),
      this.prevRepEmpCtcValue
    );
  }

  /** Previous salary + percentage difference (prev CTC + 18%). */
  get prevRepEmpCtcPlusPercentageValue(): number | null {
    if (!this.isReplacement) return null;
    return this.hrHttpService.calculateMrfPrevRepEmpCtcPlusPercentage(
      this.prevRepEmpCtcValue,
      18
    );
  }

  /** e.g. "4,000 + 4,720 %" (previous salary + prev+18% amount). */
  get prevRepEmpCtcPlusPercentageLabel(): string {
    const prev = this.prevRepEmpCtcValue;
    const withHike = this.prevRepEmpCtcPlusPercentageValue;
    if (prev == null || withHike == null) {
      return '—';
    }
    const prevLabel = this.hrHttpService.formatMrfBudgetAmount(prev);
    const hikeLabel = this.hrHttpService.formatMrfBudgetAmount(withHike);
    return `${prevLabel} + ${hikeLabel} %`;
  }

  get isReplacementSalaryOverPercentageLimit(): boolean {
    const diff = this.replacementSalaryPercentageDiff;
    return diff != null && diff > 18;
  }

  cancel(): void {
    this.router.navigate(['/hrDetails/mrfDashboard']);
  }

  saveHrStageEdits(): void {
    if (this.isSaving) return;

    const mrfId = this.getMrfFormId();
    if (!mrfId) {
      this.hrAlert.show('MRF id is missing.', 'Error');
      return;
    }

    // if (this.jobDescriptionAttached === 'no' && !this.hasJobDescriptionDocumentReady()) {
    //   this.hrAlert.show(
    //     'Work Description document is mandatory when job description is not marked as attached.',
    //     'Warning'
    //   );
    //   return;
    // }

    const payload = this.buildMisReviewPayload(mrfId, 'APPROVED');
    const uploadRequest = this.uploadPendingDocuments(mrfId);

    this.isSaving = true;
    this.spinner.show();
    uploadRequest
      .pipe(switchMap(() => this.hrHttpService.saveMisReview(payload)))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.clearPendingFiles();
          this.spinner.hide();
          this.hrAlert.showOnDashboard('Changes saved successfully.', 'Success');
          this.router.navigate(['/hrDetails/mrfDashboard']);
        },
        error: (err: unknown) => {
          this.isSaving = false;
          console.error('MIS assessment update failed', err);
          this.hrAlert.show('Failed to save changes. Please try again.', 'Error');
          this.spinner.hide();
        },
      });
  }

  private getWorkflowStatus(): string {
    return resolveMrfRequestStatusRaw(this.mrfData ?? {});
  }

  private applyMisFormFields(): void {
    this.rehirePolicyGuidelines = this.toYesNo(
      this.mrfData?.isRehirePolicy ?? this.mrfData?.rehirePolicyGuidelines
    );
    this.withinRehireCost = this.toYesNo(
      this.mrfData?.isRehireCost ?? this.mrfData?.withinRehireCost
    );
    this.isBudgetedPosition = this.resolveBudgetedPositionValue();
    this.jobDescriptionAttached = this.toYesNo(
      this.mrfData?.isJobDescription ?? this.mrfData?.jobDescriptionAttached
    );
    this.justification.setValue(String(this.mrfData?.justification ?? ''));
    this.workDescription.setValue(String(this.mrfData?.workDescription ?? ''));

    this.approvedBy.setValue('', { emitEvent: false });

    if (this.isReadOnlyMisSection) {
      this.justification.disable({ emitEvent: false });
      this.workDescription.disable({ emitEvent: false });
      this.approvedBy.disable({ emitEvent: false });
      this.approvedBy2.disable({ emitEvent: false });
    } else {
      this.justification.enable({ emitEvent: false });
      this.workDescription.enable({ emitEvent: false });
      this.approvedBy.enable({ emitEvent: false });
      this.approvedBy2.enable({ emitEvent: false });
    }
    this.syncManagementApprovedBySelection();
  }

  private toYesNo(value: unknown, fallback: 'yes' | 'no' = 'yes'): 'yes' | 'no' {
    if (value === true || value === 'true' || value === 'yes' || value === 'Y' || value === 1) {
      return 'yes';
    }
    if (value === false || value === 'false' || value === 'no' || value === 'N' || value === 0) {
      return 'no';
    }
    return fallback;
  }

  private resolveBudgetedPositionValue(): 'yes' | 'no' {
    if (this.shouldDefaultBudgetedPositionToNo()) {
      return 'no';
    }
    return this.toYesNo(
      this.mrfData?.isBudgetPosition ?? this.mrfData?.isBudgetedPosition,
      'yes'
    );
  }

  private shouldDefaultBudgetedPositionToNo(): boolean {
    if (this.isReadOnlyMisSection) {
      return false;
    }
    return this.isExpectedSalaryOverAvailableBudget;
  }

  private buildMisReviewPayload(
    mrfId: string,
    status: 'APPROVED' | 'REJECTED'
  ): Record<string, unknown> {
    return {
      mrfFormId: Number(mrfId),
      isRehirePolicy: this.rehirePolicyGuidelines === 'yes',
      isRehireCost: this.withinRehireCost === 'yes',
      isBudgetPosition: this.isBudgetedPosition === 'yes',
      isJobDescription: this.jobDescriptionAttached === 'yes',
      justification: this.justification.value ?? '',
      workDescription: this.workDescription.value ?? '',
      approveBy: this.resolveApprovedById(),
      approvedByFile: '',
      mrfFormStatus: status,
    };
  }

  /** Loads replacement / rehiring employee details on form load. */
  private loadReplacementEmployeeDetails(onComplete?: () => void): void {
    const replacementEmpId = this.resolveReplacementEmployeeId();

    let chain$: Observable<Record<string, unknown>> = of(this.mrfData);

    if (this.isReplacement && replacementEmpId) {
      chain$ = chain$.pipe(
        switchMap((data) =>
          this.hrHttpService.loadReplacementViaCurrentAndLeftSearch(data, replacementEmpId, {
            scope: 'replacement',
          })
        )
      );
    }

    if (this.isRehiring) {
      chain$ = chain$.pipe(
        switchMap((data) => this.hrHttpService.loadRehiringViaLeftEmpSearch(data))
      );
    }

    chain$.pipe(finalize(() => onComplete?.())).subscribe({
      next: (merged) => {
        this.mrfData = merged;
        this.partitionDocuments();
        this.applyApprovedByOptionsForCurrentData();
      },
      error: (err) => console.error(err),
    });
  }

  private loadTempEmployeeDetailsIfNeeded(onComplete?: () => void): void {
    if (!this.isExamTaken('yes')) {
      onComplete?.();
      return;
    }

    const tempId = this.fieldValue('employeeTempId', 'empTempId', 'tempEmpId', 'tempPayrollId');
    if (!tempId || tempId.length < 5) {
      onComplete?.();
      return;
    }

    this.hrHttpService
      .wrapMrfLoaderApi(this.hrHttpService.searchTempEmployees(tempId), null)
      .pipe(finalize(() => onComplete?.()))
      .subscribe({
        next: (res) => {
          const list = this.hrHttpService.normalizeTempEmpSearchResults(res);
          const match = this.hrHttpService.findTempEmpSearchMatch(list, tempId);
          if (match) {
            this.mrfData = { ...this.mrfData, ...this.hrHttpService.mapTempEmpToViewData(match) };
          }
        },
        error: (err) => console.error(err),
      });
  }

  private resolveReplacementEmployeeId(): string {
    return this.hrHttpService.resolveMrfReplacementEmpId(this.mrfData ?? {});
  }

  private initExamTakenFromData(): void {
    const v = this.mrfData?.examTaken ?? this.mrfData?.isExamTaken ?? this.mrfData?.examTakenFlag;
    if (v === true || v === 'true' || v === 'yes' || v === 'Y' || v === 1) {
      this.examTakenDisplay = 'yes';
    } else {
      this.examTakenDisplay = 'no';
    }
  }

  private partitionDocuments(): void {
    const buckets = this.hrHttpService.partitionMrfDocuments(this.documents, {
      examTaken: this.isExamTaken('yes'),
    });
    this.aadhaarDocuments = buckets.aadhaar;
    this.panDocuments = buckets.pan;
    this.examDocuments = buckets.exam;
    this.jobDescDocuments = buckets.workDescription;
    this.approvalDocuments = buckets.approvedByManagement;
    this.attachedDocuments = buckets.attached;
  }

  onFilesSelected(event: Event, type: 'job' | 'approval'): void {
    if (!this.canEditWorkDescAndApprovalDocs) return;
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;

    const sizeMessage = setMrfUploadSizeError(this.uploadFileErrors, type, files);
    if (sizeMessage) {
      this.hrAlert.show(sizeMessage, 'Warning');
      input.value = '';
      return;
    }

    const invalid = files.filter((file) => !this.hrHttpService.isMrfPdfOrImageFile(file));
    if (invalid.length) {
      this.hrAlert.show('Only PDF and image files are allowed.', 'Warning');
      input.value = '';
      return;
    }

    const current = this.getPendingFiles(type);
    const remaining = this.maxPendingFiles - current.length;
    if (remaining <= 0) {
      this.hrAlert.show(`You can attach up to ${this.maxPendingFiles} files.`, 'Warning');
      input.value = '';
      return;
    }

    const toAdd = files.slice(0, remaining);
    if (files.length > remaining) {
      this.hrAlert.show(
        `Only ${remaining} more file(s) allowed (maximum ${this.maxPendingFiles}).`,
        'Warning'
      );
    }

    clearMrfUploadSizeError(this.uploadFileErrors, type);
    this.setPendingFiles(type, [...current, ...toAdd]);
    input.value = '';
  }

  getUploadFileError(type: 'job' | 'approval'): string {
    return getMrfUploadSizeError(this.uploadFileErrors, type);
  }

  removePendingFile(type: 'job' | 'approval', index: number): void {
    const current = this.getPendingFiles(type);
    this.setPendingFiles(
      type,
      current.filter((_, i) => i !== index)
    );
    clearMrfUploadSizeError(this.uploadFileErrors, type);
  }

  canAddMoreFiles(type: 'job' | 'approval'): boolean {
    return this.getPendingFiles(type).length < this.maxPendingFiles;
  }

  onAttachClick(event: Event, type: 'job' | 'approval', fileInput: HTMLInputElement): void {
    if (!this.canAddMoreFiles(type)) {
      event.preventDefault();
      return;
    }
    fileInput.click();
  }

  private getPendingFiles(type: 'job' | 'approval'): File[] {
    return type === 'job' ? this.jobDescPendingFiles : this.approvedByPendingFiles;
  }

  private setPendingFiles(type: 'job' | 'approval', files: File[]): void {
    if (type === 'job') {
      this.jobDescPendingFiles = files;
    } else {
      this.approvedByPendingFiles = files;
    }
  }

  getDocumentName(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentViewName(doc);
  }

  getDocumentUrl(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentViewUrl(doc);
  }

  canDownloadDocument(doc: MrfDocumentView): boolean {
    return this.hrHttpService.canDownloadMrfDocument(doc);
  }

  onDocumentDownload(doc: MrfDocumentView): void {
    this.spinner.show();
    this.hrHttpService
      .triggerMrfDocumentDownload(doc)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        error: (err) => {
          console.error(err);
          this.hrAlert.show('Failed to download document.', 'Error');
        },
      });
  }

  getDocumentIconLabel(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentIconLabel(doc);
  }

  isImageFile(doc: any): boolean {
    return this.hrHttpService.isMrfImageDocument(doc);
  }

  getManagementApprovedByDisplayLabel(): string {
    const label = this.hrHttpService.getMrfManagementApprovedByDisplayFromData(
      this.mrfData ?? {},
      this.approvedByOptionsList
    );
    return label || '—';
  }

  getApprovedByOptionLabel(option: MrfApprovedByOption): string {
    return this.hrHttpService.getMrfApprovedByOptionLabel(option);
  }

  private initFilteredOptions(): void {
    this.approvedByOptions = combineLatest([
      this.approvedBy.valueChanges.pipe(startWith('')),
      this.approvedByList$,
    ]).pipe(
      map(([value, list]) => {
        const search = typeof value === 'string' ? value : '';
        return this.hrHttpService.filterMrfApprovedByOptions(list, search);
      })
    );
  }

  private setApprovedByOptionsList(list: MrfApprovedByOption[]): void {
    this.approvedByOptionsList = list;
    this.approvedByList$.next(list);
    this.syncManagementApprovedBySelection();
  }

  private setApprovedByBaseList(list: MrfApprovedByOption[]): void {
    this.approvedByBaseList = list;
    this.applyApprovedByOptionsForCurrentData();
  }

  private applyApprovedByOptionsForCurrentData(): void {
    const merged = this.hrHttpService.buildMrfApprovedByOptionsForManagementSelection(
      this.approvedByBaseList,
      this.mrfData ?? {}
    );
    this.setApprovedByOptionsList(merged);
  }

  formatBudgetAmount(value: number | null): string {
    return this.hrHttpService.formatMrfBudgetAmount(value);
  }

  private applyBudgetedPositionDefaultFromAvailableBudget(): void {
    if (this.shouldDefaultBudgetedPositionToNo()) {
      this.isBudgetedPosition = 'no';
    }
  }

  onApprovedBySelectionChange(selectedName: string): void {
    this.approvedBy.setValue('', { emitEvent: false });
    this.approvedBy2.setValue(selectedName ?? '', { emitEvent: false });
  }

  private syncManagementApprovedBySelection(): void {
    if (!this.mrfData) return;

    const managementName = this.hrHttpService.resolveMrfManagementApprovedBySelectValue(
      this.mrfData,
      this.approvedByOptionsList
    );
    this.approvedBy.setValue('', { emitEvent: false });
    this.approvedBy2.setValue(managementName, { emitEvent: false });
  }

  private loadHeadCountFromData(onComplete?: () => void): void {
    if (this.mrfData?.campusId == null) {
      onComplete?.();
      return;
    }

    this.hrHttpService
      .wrapMrfLoaderApi(this.hrHttpService.getMrfHeadCountInfoForMrfData(this.mrfData ?? {}), null)
      .pipe(finalize(() => onComplete?.()))
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          const totalEmp = Number(data?.totalEmpCount ?? 0);
          this.budgetedPositions = String(totalEmp);
          const headcount = Number(this.mrfData?.noOfHeadcount ?? this.mrfData?.headcount ?? 1);
          this.noOfHeadcount = String(headcount);
          this.availableHeadcount = String(Math.max(0, totalEmp - headcount));
        },
        error: (err) => console.error(err),
      });
  }

  private applyAvailableBudgetView(budget: MrfAvailableBudgetView | null): void {
    if (!budget) {
      this.budgetAmount = null;
      this.totalUsedBudget = null;
      this.totalExpectedSalBudget = null;
      this.availableBudgetAmount = null;
      return;
    }
    this.budgetAmount = budget.budgetAmount;
    this.totalUsedBudget = budget.totalUsedBudget;
    this.totalExpectedSalBudget = budget.totalExpectedSal;
    this.availableBudgetAmount = budget.availableBudget;
  }

  get justificationCount(): number {
    return (this.justification.value ?? '').length;
  }

  get workDescriptionCount(): number {
    return (this.workDescription.value ?? '').length;
  }

  hasJobDescriptionDocumentReady(): boolean {
    if (this.jobDescriptionAttached !== 'no') {
      return true;
    }
    return this.jobDescDocuments.length > 0 || this.jobDescPendingFiles.length > 0;
  }

  private Filter(value: string, list: any[], field: string): any[] {
    const search = value.trim().toLowerCase();
    return list.filter((item) =>
      item[field]?.toString().trim().toLowerCase().includes(search)
    );
  }

  submitMisReview(status: 'APPROVED' | 'REJECTED'): void {
    if (this.isSaving) return;

    const mrfId = this.getMrfFormId();
    if (!mrfId) {
      this.hrAlert.show('MRF id is missing.', 'Error');
      return;
    }

    // if (
    //   status === 'APPROVED' &&
    //   this.jobDescriptionAttached === 'no' &&
    //   !this.hasJobDescriptionDocumentReady()
    // ) {
    //   this.hrAlert.show(
    //     'Work Description document is mandatory when job description is not marked as attached.',
    //     'Warning'
    //   );
    //   return;
    // }

    const payload = this.buildMisReviewPayload(mrfId, status);
    const uploadRequest = this.uploadPendingDocuments(mrfId);

    this.isSaving = true;
    this.spinner.show();
    uploadRequest
      .pipe(switchMap(() => this.hrHttpService.saveMisReview(payload)))
      .subscribe({
        next: (res: unknown) => {
          this.isSaving = false;
          this.clearPendingFiles();
          console.log('MIS Review saved', res);
          this.hrAlert.showOnDashboard(
            status === 'APPROVED' ? 'MRF approved successfully.' : 'MRF rejected.',
            'Success'
          );
          this.spinner.hide();
          this.router.navigate(['/hrDetails/mrfDashboard']);
        },
        error: (err: unknown) => {
          this.isSaving = false;
          console.error('MIS Review failed', err);
          this.hrAlert.show('Failed to submit. Please try again.', 'Error');
          this.spinner.hide();
        },
      });
  }

  private uploadPendingDocuments(mrfId: string): Observable<unknown> {
    const uploads: Observable<unknown>[] = [];

    if (this.jobDescPendingFiles.length) {
      uploads.push(
        this.hrHttpService.uploadMrfDocuments(
          mrfId,
          this.jobDescPendingFiles,
          MRF_DOCUMENT_TYPE.WORK_DESCRIPTION
        )
      );
    }

    if (this.approvedByPendingFiles.length) {
      uploads.push(
        this.hrHttpService.uploadMrfDocuments(
          mrfId,
          this.approvedByPendingFiles,
          MRF_DOCUMENT_TYPE.APPROVED_BY_MANAGEMENT
        )
      );
    }

    if (!uploads.length) {
      return of(undefined);
    }

    return from(uploads).pipe(concatMap((upload) => upload));
  }

  private getAllPendingFiles(): File[] {
    return [...this.jobDescPendingFiles, ...this.approvedByPendingFiles];
  }

  private clearPendingFiles(): void {
    this.jobDescPendingFiles = [];
    this.approvedByPendingFiles = [];
  }

  private resolveApprovedById(): number | null {
    return this.hrHttpService.resolveMrfManagementApprovedByIdForSubmit(
      this.mrfData ?? {},
      String(this.approvedBy2.value ?? ''),
      this.approvedByOptionsList
    );
  }
}
